import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Container,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  GridOn as GridIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  RestartAlt as RestartIcon,
  DragIndicator as DragIcon,
  TextFields as TextIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components
const CanvasContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.grey[50],
  margin: "0 auto",
  maxHeight: "600px",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  cursor: "move",
  border: `2px solid ${theme.palette.primary.main}`,
  "&.dragging": {
    border: `2px solid ${theme.palette.primary.dark}`,
  },
}));

const TextOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  pointerEvents: "none",
  userSelect: "none",
  zIndex: 30,
  padding: "2px 4px",
  borderRadius: "3px",
  fontFamily: "Arial, sans-serif",
  fontWeight: 500,
  whiteSpace: "nowrap",
}));

const GridOverlaySvg = styled("svg")({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 10,
});

interface LogoPosition {
  x: number;
  y: number;
}

interface LogoSize {
  width: number;
  height: number;
}

// interface SizeUnit {
//   unit: "px" | "in" | "cm";
//   width: number;
//   height: number;
// }

interface DragOffset {
  x: number;
  y: number;
}

interface TextStyle {
  fontSize: number;
  color: string;
  backgroundColor: string;
}

interface CustomTextPosition {
  x: number;
  y: number;
}

interface TextData {
  textContent: string;
  textX: number;
  textY: number;
}

const MockupGenerator: React.FC = () => {
  const [backgroundImage, setBackgroundImage] =
    useState<HTMLImageElement | null>(null);
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>({
    x: 50,
    y: 50,
  });
  const [logoSize, setLogoSize] = useState<LogoSize>({
    width: 100,
    height: 100,
  });
  const [sizeUnit, setSizeUnit] = useState<"px" | "in" | "cm">("px");
  const [maintainAspectRatio, setMaintainAspectRatio] = useState<boolean>(true);
  const [debugMode] = useState<boolean>(false);
  const [logoRotation, setLogoRotation] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 });

  // Input field states for temporary values
  const [positionInputs, setPositionInputs] = useState({ x: "50", y: "50" });
  const [sizeInputs, setSizeInputs] = useState({ width: "100", height: "100" });
  const [rotationInput, setRotationInput] = useState("0");
  const [fontSizeInput, setFontSizeInput] = useState("14");
  const [customPositionInputs, setCustomPositionInputs] = useState({
    x: "0",
    y: "0",
  });

  // Text display options
  const [showSizeText, setShowSizeText] = useState<boolean>(true);
  const [textPosition, setTextPosition] = useState<
    "below" | "above" | "left" | "right" | "custom"
  >("below");
  const [customTextPosition, setCustomTextPosition] =
    useState<CustomTextPosition>({ x: 0, y: 0 });
  const [textStyle, setTextStyle] = useState<TextStyle>({
    fontSize: 14,
    color: "#000000",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "background" | "logo"
  ): void => {
    const file = event.target.files?.[0];
    if (file && ["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          if (type === "background") {
            setBackgroundImage(img);
          } else {
            setLogoImage(img);
            // Auto-size logo to reasonable dimensions while maintaining aspect ratio
            const aspectRatio = img.width / img.height;
            const maxSize = 100; // Maximum dimension in pixels

            if (img.width > img.height) {
              // Landscape image
              setLogoSize({
                width: maxSize,
                height: Math.round(maxSize / aspectRatio),
              });
            } else {
              // Portrait or square image
              setLogoSize({
                width: Math.round(maxSize * aspectRatio),
                height: maxSize,
              });
            }
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload PNG, JPG, or WebP images only.");
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!logoImage) return;

    setIsDragging(true);
    const rect = containerRef.current?.getBoundingClientRect();
    const logoRect = logoRef.current?.getBoundingClientRect();

    if (rect && logoRect) {
      setDragOffset({
        x: e.clientX - logoRect.left,
        y: e.clientY - logoRect.top,
      });
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent): void => {
      if (!isDragging || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      let newX = e.clientX - rect.left - dragOffset.x;
      let newY = e.clientY - rect.top - dragOffset.y;

      // Snap to grid
      newX = Math.round(newX / 10) * 10;
      newY = Math.round(newY / 10) * 10;

      // Keep logo within bounds
      const maxX = rect.width - logoSize.width;
      const maxY = rect.height - logoSize.height;

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));

      setLogoPosition({ x: newX, y: newY });
    },
    [isDragging, dragOffset, logoSize]
  );

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  // Custom text positioning handler
  const handleMouseDownCustomText = (e: React.MouseEvent): void => {
    if (e.button === 0 && textPosition === "custom") {
      // Left click for custom text positioning
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        setCustomTextPosition({ x: clickX, y: clickY });
      }
    }
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove]);

  // Sync input states with actual values when they change
  React.useEffect(() => {
    setPositionInputs({
      x: logoPosition.x.toString(),
      y: logoPosition.y.toString(),
    });
  }, [logoPosition]);

  React.useEffect(() => {
    setSizeInputs({
      width: getDisplaySize("width").toFixed(sizeUnit === "px" ? 0 : 2),
      height: getDisplaySize("height").toFixed(sizeUnit === "px" ? 0 : 2),
    });
  }, [logoSize, sizeUnit]);

  React.useEffect(() => {
    setRotationInput(logoRotation.toString());
  }, [logoRotation]);

  React.useEffect(() => {
    setFontSizeInput(textStyle.fontSize.toString());
  }, [textStyle.fontSize]);

  React.useEffect(() => {
    setCustomPositionInputs({
      x: customTextPosition.x.toString(),
      y: customTextPosition.y.toString(),
    });
  }, [customTextPosition]);

  // Conversion functions
  const pxToInches = (px: number): number => px / 96; // 96 DPI
  const pxToCm = (px: number): number => px / 37.795; // 37.795 pixels per cm
  const inchesToPx = (inches: number): number => inches * 96;
  const cmToPx = (cm: number): number => cm * 37.795;

  const convertSize = (
    value: number,
    fromUnit: "px" | "in" | "cm",
    toUnit: "px" | "in" | "cm"
  ): number => {
    if (fromUnit === toUnit) return value;

    // Convert to pixels first
    let pxValue: number;
    if (fromUnit === "px") pxValue = value;
    else if (fromUnit === "in") pxValue = inchesToPx(value);
    else pxValue = cmToPx(value);

    // Convert from pixels to target unit
    if (toUnit === "px") return pxValue;
    else if (toUnit === "in") return pxToInches(pxValue);
    else return pxToCm(pxValue);
  };

  const handleSizeChange = (dimension: keyof LogoSize, value: string): void => {
    const numValue = parseFloat(value) || 0;
    const minValue = sizeUnit === "px" ? 1 : sizeUnit === "in" ? 0.1 : 0.1;

    if (numValue < minValue) return;

    // Convert the input value to pixels
    const pxValue = convertSize(numValue, sizeUnit, "px");

    // If logo image exists and aspect ratio should be maintained
    if (logoImage && maintainAspectRatio) {
      const aspectRatio = logoImage.width / logoImage.height;

      if (dimension === "width") {
        setLogoSize({
          width: pxValue,
          height: Math.round(pxValue / aspectRatio),
        });
      } else {
        setLogoSize({
          width: Math.round(pxValue * aspectRatio),
          height: pxValue,
        });
      }
    } else {
      // Update only the specified dimension
      setLogoSize((prev) => ({
        ...prev,
        [dimension]: pxValue,
      }));
    }
  };

  const handleUnitChange = (newUnit: "px" | "in" | "cm"): void => {
    setSizeUnit(newUnit);
  };

  // Get display values for current unit
  const getDisplaySize = (dimension: keyof LogoSize): number => {
    return convertSize(logoSize[dimension], "px", sizeUnit);
  };

  const exportImage = (): void => {
    if (!backgroundImage) {
      alert("Please upload a background image first.");
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to match background image dimensions (full resolution)
    canvas.width = backgroundImage.naturalWidth;
    canvas.height = backgroundImage.naturalHeight;

    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Draw background image at full resolution
    ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

    // Draw logo if it exists
    if (logoImage && containerRef.current) {
      // Get the actual display dimensions of the container
      const containerRect = containerRef.current.getBoundingClientRect();
      const displayWidth = containerRect.width;
      const displayHeight = containerRect.height;

      // Calculate the scale factor between display and original image
      const scaleX = backgroundImage.naturalWidth / displayWidth;
      const scaleY = backgroundImage.naturalHeight / displayHeight;

      // Calculate the exact logo position in the original image coordinates
      // We need to account for the fact that the background image is displayed with object-fit: contain
      const backgroundAspectRatio =
        backgroundImage.naturalWidth / backgroundImage.naturalHeight;
      const containerAspectRatio = displayWidth / displayHeight;

      let actualDisplayWidth = displayWidth;
      let actualDisplayHeight = displayHeight;
      let offsetX = 0;
      let offsetY = 0;

      if (backgroundAspectRatio > containerAspectRatio) {
        // Background is wider than container, so it's scaled to fit width
        actualDisplayHeight = displayWidth / backgroundAspectRatio;
        offsetY = (displayHeight - actualDisplayHeight) / 2;
      } else {
        // Background is taller than container, so it's scaled to fit height
        actualDisplayWidth = displayHeight * backgroundAspectRatio;
        offsetX = (displayWidth - actualDisplayWidth) / 2;
      }

      // Calculate the actual scale factors for the visible part of the image
      const actualScaleX = backgroundImage.naturalWidth / actualDisplayWidth;
      const actualScaleY = backgroundImage.naturalHeight / actualDisplayHeight;

      // Adjust logo position to account for the offset and use the correct scale
      const adjustedX = logoPosition.x - offsetX;
      const adjustedY = logoPosition.y - offsetY;

      const scaledX = adjustedX * actualScaleX;
      const scaledY = adjustedY * actualScaleY;
      const scaledWidth = logoSize.width * actualScaleX;
      const scaledHeight = logoSize.height * actualScaleY;

      // Save context for rotation
      ctx.save();

      // Move to logo center for rotation
      const centerX = scaledX + scaledWidth / 2;
      const centerY = scaledY + scaledHeight / 2;
      ctx.translate(centerX, centerY);
      ctx.rotate((logoRotation * Math.PI) / 180);

      // Draw logo centered at origin with high quality
      ctx.drawImage(
        logoImage,
        -scaledWidth / 2,
        -scaledHeight / 2,
        scaledWidth,
        scaledHeight
      );

      // Draw size text if enabled
      if (showSizeText) {
        const textContent = `${getDisplaySize("width").toFixed(
          sizeUnit === "px" ? 0 : 2
        )}${sizeUnit}×${getDisplaySize("height").toFixed(
          sizeUnit === "px" ? 0 : 2
        )}${sizeUnit}`;
        const fontSize = textStyle.fontSize * scaleX;

        ctx.font = `${fontSize}px Arial`;
        ctx.fillStyle = textStyle.backgroundColor;
        ctx.strokeStyle = textStyle.color;
        ctx.lineWidth = 1;

        const textMetrics = ctx.measureText(textContent);
        const textWidth = textMetrics.width;
        const textHeight = fontSize;

        // Calculate text position
        let textX = 0;
        let textY = 0;

        if (textPosition === "below") {
          textX = -textWidth / 2;
          textY = scaledHeight / 2 + textHeight + 5 * scaleY;
        } else if (textPosition === "above") {
          textX = -textWidth / 2;
          textY = -scaledHeight / 2 - 5 * scaleY;
        } else if (textPosition === "left") {
          textX = -scaledWidth / 2 - textWidth - 5 * scaleX;
          textY = textHeight / 2;
        } else if (textPosition === "right") {
          textX = scaledWidth / 2 + 5 * scaleX;
          textY = textHeight / 2;
        } else if (textPosition === "custom") {
          // Convert custom text position to actual coordinates using the same logic as logo
          const backgroundAspectRatio =
            backgroundImage.naturalWidth / backgroundImage.naturalHeight;
          const containerAspectRatio = displayWidth / displayHeight;

          let actualDisplayWidth = displayWidth;
          let actualDisplayHeight = displayHeight;
          let offsetX = 0;
          let offsetY = 0;

          if (backgroundAspectRatio > containerAspectRatio) {
            actualDisplayHeight = displayWidth / backgroundAspectRatio;
            offsetY = (displayHeight - actualDisplayHeight) / 2;
          } else {
            actualDisplayWidth = displayHeight * backgroundAspectRatio;
            offsetX = (displayWidth - actualDisplayWidth) / 2;
          }

          const actualScaleX =
            backgroundImage.naturalWidth / actualDisplayWidth;
          const actualScaleY =
            backgroundImage.naturalHeight / actualDisplayHeight;

          const adjustedCustomX = customTextPosition.x - offsetX;
          const adjustedCustomY = customTextPosition.y - offsetY;
          const adjustedLogoX = logoPosition.x - offsetX;
          const adjustedLogoY = logoPosition.y - offsetY;

          textX = (adjustedCustomX - adjustedLogoX) * actualScaleX;
          textY =
            (adjustedCustomY - adjustedLogoY) * actualScaleY + textHeight / 2;
        }

        // Draw text background
        ctx.fillRect(
          textX - 2,
          textY - textHeight,
          textWidth + 4,
          textHeight + 4
        );

        // Draw text
        ctx.fillStyle = textStyle.color;
        ctx.fillText(textContent, textX, textY);
      }

      ctx.restore();
    }

    // Export as high-quality PNG
    const link = document.createElement("a");
    link.download = "composite-image.png";
    link.href = canvas.toDataURL("image/png", 1.0); // Maximum quality
    link.click();
  };

  const calculateTextPosition = (): TextData | null => {
    if (!showSizeText) return null;

    const textContent = `${getDisplaySize("width").toFixed(
      sizeUnit === "px" ? 0 : 2
    )}${sizeUnit}×${getDisplaySize("height").toFixed(
      sizeUnit === "px" ? 0 : 2
    )}${sizeUnit}`;
    let textX = 0;
    let textY = 0;

    if (textPosition === "below") {
      textX = logoPosition.x + logoSize.width / 2;
      textY = logoPosition.y + logoSize.height + 20;
    } else if (textPosition === "above") {
      textX = logoPosition.x + logoSize.width / 2;
      textY = logoPosition.y - 5;
    } else if (textPosition === "left") {
      textX = logoPosition.x - 5;
      textY = logoPosition.y + logoSize.height / 2 + textStyle.fontSize / 2;
    } else if (textPosition === "right") {
      textX = logoPosition.x + logoSize.width + 5;
      textY = logoPosition.y + logoSize.height / 2 + textStyle.fontSize / 2;
    } else if (textPosition === "custom") {
      // For custom position, use the stored coordinates directly
      textX = customTextPosition.x;
      textY = customTextPosition.y;
    }

    return { textContent, textX, textY };
  };

  const GridOverlay: React.FC = () => {
    if (!showGrid || !backgroundImage) return null;

    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return null;

    const lines: JSX.Element[] = [];
    const width = containerRef.current?.offsetWidth || 0;
    const height = containerRef.current?.offsetHeight || 0;

    // Vertical lines
    for (let x = 0; x <= width; x += 10) {
      lines.push(
        <line
          key={`v-${x}`}
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          stroke="#00ff00"
          strokeWidth="0.5"
          opacity="0.3"
        />
      );
    }

    // Horizontal lines
    for (let y = 0; y <= height; y += 10) {
      lines.push(
        <line
          key={`h-${y}`}
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke="#00ff00"
          strokeWidth="0.5"
          opacity="0.3"
        />
      );
    }

    // Add debug overlay to show actual image boundaries
    if (debugMode) {
      const backgroundAspectRatio =
        backgroundImage.naturalWidth / backgroundImage.naturalHeight;
      const containerAspectRatio = width / height;

      let actualDisplayWidth = width;
      let actualDisplayHeight = height;
      let offsetX = 0;
      let offsetY = 0;

      if (backgroundAspectRatio > containerAspectRatio) {
        actualDisplayHeight = width / backgroundAspectRatio;
        offsetY = (height - actualDisplayHeight) / 2;
      } else {
        actualDisplayWidth = height * backgroundAspectRatio;
        offsetX = (width - actualDisplayWidth) / 2;
      }

      // Draw rectangle showing actual image boundaries
      lines.push(
        <rect
          key="image-boundary"
          x={offsetX}
          y={offsetY}
          width={actualDisplayWidth}
          height={actualDisplayHeight}
          stroke="#ff0000"
          strokeWidth="2"
          fill="none"
          opacity="0.7"
        />
      );

      // Add text showing the offset values
      lines.push(
        <text
          key="offset-info"
          x="10"
          y="20"
          fill="#ff0000"
          fontSize="12"
          fontWeight="bold"
        >
          {`Offset: (${offsetX.toFixed(1)}, ${offsetY.toFixed(1)}) | Scale: ${(
            backgroundImage.naturalWidth / actualDisplayWidth
          ).toFixed(2)}x`}
        </text>
      );
    }

    return (
      <GridOverlaySvg width={width} height={height}>
        {lines}
      </GridOverlaySvg>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.100", p: 4 }}>
      <Container maxWidth="xl">
        <Typography
          variant="h2"
          component="h1"
          align="center"
          gutterBottom
          sx={{ mb: 4, color: "grey.800" }}
        >
          Mockup Generator
        </Typography>

        {/* Upload Controls */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Background Image (PNG, JPG, WebP)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Choose Background Image
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={(e) => handleImageUpload(e, "background")}
                  hidden
                />
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Logo Image (PNG, JPG, WebP)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ height: 56 }}
              >
                Choose Logo Image
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={(e) => handleImageUpload(e, "logo")}
                  hidden
                />
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Canvas Area */}
        {backgroundImage && (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5" component="h2">
                Canvas
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                      icon={<GridIcon />}
                      checkedIcon={<GridIcon />}
                    />
                  }
                  label="Show Grid (10px)"
                />
                {/* <FormControlLabel
                   control={
                     <Checkbox
                       checked={debugMode}
                       onChange={(e) => setDebugMode(e.target.checked)}
                     />
                   }
                   label="Debug Mode"
                 /> */}

                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={exportImage}
                >
                  Download Composite
                </Button>
              </Stack>
            </Box>

            {textPosition === "custom" && showSizeText && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Click anywhere on the canvas to position the text
              </Alert>
            )}

            <CanvasContainer
              ref={containerRef}
              onMouseDown={handleMouseDownCustomText}
              sx={{
                width: "800px",
                height: `${
                  (backgroundImage.height * 800) / backgroundImage.width
                }px`,
                maxHeight: "600px",
                overflow: "hidden",
                position: "relative",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                {/* Background Image */}
                <img
                  src={backgroundImage.src}
                  alt="Background"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                  draggable={false}
                />

                {/* Grid Overlay */}
                <GridOverlay />

                {/* Logo */}
                {logoImage && (
                  <>
                    <LogoContainer
                      ref={logoRef}
                      className={isDragging ? "dragging" : ""}
                      sx={{
                        left: `${logoPosition.x}px`,
                        top: `${logoPosition.y}px`,
                        width: `${logoSize.width}px`,
                        height: `${logoSize.height}px`,
                        transform: `rotate(${logoRotation}deg)`,
                        transformOrigin: "center",
                        zIndex: 20,
                      }}
                      onMouseDown={handleMouseDown}
                    >
                      <img
                        src={logoImage.src}
                        alt="Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                        draggable={false}
                      />
                    </LogoContainer>

                    {/* Size Text Display */}
                    {showSizeText &&
                      (() => {
                        const textData = calculateTextPosition();
                        if (!textData) return null;

                        return (
                          <TextOverlay
                            sx={{
                              left: `${textData.textX}px`,
                              top: `${textData.textY}px`,
                              fontSize: `${textStyle.fontSize}px`,
                              color: textStyle.color,
                              backgroundColor: textStyle.backgroundColor,
                              transform:
                                textPosition === "above" ||
                                textPosition === "below"
                                  ? "translateX(-50%)"
                                  : textPosition === "left"
                                  ? "translateX(-100%)"
                                  : "none",
                            }}
                          >
                            {textData.textContent}
                          </TextOverlay>
                        );
                      })()}
                  </>
                )}
              </Box>
            </CanvasContainer>
          </Paper>
        )}

        {/* Controls Panel */}
        {logoImage && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" component="h3" gutterBottom>
              Logo Controls
            </Typography>

            <Grid container spacing={3}>
              {/* Position */}
              <Grid item xs={12} md={6} lg={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Position (px)
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="X"
                        type="number"
                        value={positionInputs.x}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPositionInputs((prev) => ({ ...prev, x: value }));
                          if (value !== "") {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue)) {
                              setLogoPosition((prev) => ({
                                ...prev,
                                x: numValue,
                              }));
                            }
                          }
                        }}
                        onBlur={() => {
                          if (positionInputs.x === "") {
                            setPositionInputs((prev) => ({
                              ...prev,
                              x: logoPosition.x.toString(),
                            }));
                          }
                        }}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="Y"
                        type="number"
                        value={positionInputs.y}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPositionInputs((prev) => ({ ...prev, y: value }));
                          if (value !== "") {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue)) {
                              setLogoPosition((prev) => ({
                                ...prev,
                                y: numValue,
                              }));
                            }
                          }
                        }}
                        onBlur={() => {
                          if (positionInputs.y === "") {
                            setPositionInputs((prev) => ({
                              ...prev,
                              y: logoPosition.y.toString(),
                            }));
                          }
                        }}
                        size="small"
                        fullWidth
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Size */}
              <Grid item xs={12} md={6} lg={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Size (px)
                    </Typography>
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={maintainAspectRatio}
                            onChange={(e) =>
                              setMaintainAspectRatio(e.target.checked)
                            }
                          />
                        }
                        label="Maintain aspect ratio"
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select
                          value={sizeUnit}
                          onChange={(e) =>
                            handleUnitChange(
                              e.target.value as "px" | "in" | "cm"
                            )
                          }
                          label="Unit"
                        >
                          <MenuItem value="px">Pixels (px)</MenuItem>
                          <MenuItem value="in">Inches (in)</MenuItem>
                          <MenuItem value="cm">Centimeters (cm)</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Width"
                        type="number"
                        value={sizeInputs.width}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSizeInputs((prev) => ({ ...prev, width: value }));
                          if (value !== "") {
                            handleSizeChange("width", value);
                          }
                        }}
                        onBlur={() => {
                          if (sizeInputs.width === "") {
                            setSizeInputs((prev) => ({
                              ...prev,
                              width: getDisplaySize("width").toFixed(
                                sizeUnit === "px" ? 0 : 2
                              ),
                            }));
                          }
                        }}
                        inputProps={{
                          min: sizeUnit === "px" ? 1 : 0.1,
                          step: sizeUnit === "px" ? 1 : 0.1,
                        }}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="Height"
                        type="number"
                        value={sizeInputs.height}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSizeInputs((prev) => ({ ...prev, height: value }));
                          if (value !== "") {
                            handleSizeChange("height", value);
                          }
                        }}
                        onBlur={() => {
                          if (sizeInputs.height === "") {
                            setSizeInputs((prev) => ({
                              ...prev,
                              height: getDisplaySize("height").toFixed(
                                sizeUnit === "px" ? 0 : 2
                              ),
                            }));
                          }
                        }}
                        inputProps={{
                          min: sizeUnit === "px" ? 1 : 0.1,
                          step: sizeUnit === "px" ? 1 : 0.1,
                        }}
                        size="small"
                        fullWidth
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Rotation */}
              <Grid item xs={12} md={6} lg={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Rotation
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="Angle (°)"
                        type="number"
                        value={rotationInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRotationInput(value);
                          if (value !== "") {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue)) {
                              setLogoRotation(numValue);
                            }
                          }
                        }}
                        onBlur={() => {
                          if (rotationInput === "") {
                            setRotationInput(logoRotation.toString());
                          }
                        }}
                        inputProps={{ min: -180, max: 180 }}
                        size="small"
                        fullWidth
                      />
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          onClick={() => setLogoRotation((prev) => prev - 90)}
                          title="Rotate -90°"
                          size="small"
                        >
                          <RotateLeftIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => setLogoRotation(0)}
                          title="Reset rotation"
                          size="small"
                        >
                          <RestartIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => setLogoRotation((prev) => prev + 90)}
                          title="Rotate +90°"
                          size="small"
                        >
                          <RotateRightIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6} lg={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Current Status
                    </Typography>
                    <Stack spacing={1}>
                      <Chip
                        label={`Coordinates: (${logoPosition.x}, ${logoPosition.y})`}
                        size="small"
                      />
                      <Chip
                        label={`Size: ${getDisplaySize("width").toFixed(
                          sizeUnit === "px" ? 0 : 2
                        )}${sizeUnit} × ${getDisplaySize("height").toFixed(
                          sizeUnit === "px" ? 0 : 2
                        )}${sizeUnit}`}
                        size="small"
                      />
                      <Chip label={`Rotation: ${logoRotation}°`} size="small" />
                      <Chip
                        label={`Grid: ${showGrid ? "Enabled" : "Disabled"}`}
                        size="small"
                        color={showGrid ? "success" : "default"}
                      />
                      <Chip
                        label={isDragging ? "Dragging..." : "Ready"}
                        size="small"
                        color={isDragging ? "primary" : "default"}
                        icon={isDragging ? <DragIcon /> : undefined}
                      />
                      {debugMode && (
                        <>
                          <Chip
                            label={`Display: ${
                              containerRef.current?.offsetWidth || 0
                            }×${containerRef.current?.offsetHeight || 0}`}
                            size="small"
                            color="secondary"
                          />
                          <Chip
                            label={`Original: ${
                              backgroundImage?.naturalWidth || 0
                            }×${backgroundImage?.naturalHeight || 0}`}
                            size="small"
                            color="secondary"
                          />
                          <Chip
                            label={`Scale: ${(
                              (backgroundImage?.naturalWidth || 1) /
                              (containerRef.current?.offsetWidth || 1)
                            ).toFixed(2)}x`}
                            size="small"
                            color="secondary"
                          />
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Text Display Controls */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Size Text Display
            </Typography>

            <Grid container spacing={3}>
              {/* Text Toggle & Position */}
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSizeText}
                      onChange={(e) => setShowSizeText(e.target.checked)}
                    />
                  }
                  label="Show size text"
                />

                {showSizeText && (
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Position</InputLabel>
                    <Select
                      value={textPosition}
                      onChange={(e) =>
                        setTextPosition(
                          e.target.value as
                            | "below"
                            | "above"
                            | "left"
                            | "right"
                            | "custom"
                        )
                      }
                      label="Position"
                    >
                      <MenuItem value="below">Below logo</MenuItem>
                      <MenuItem value="above">Above logo</MenuItem>
                      <MenuItem value="left">Left of logo</MenuItem>
                      <MenuItem value="right">Right of logo</MenuItem>
                      <MenuItem value="custom">
                        Custom position (click to place)
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>

              {/* Custom Position */}
              {showSizeText && textPosition === "custom" && (
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Custom Position
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      type="number"
                      placeholder="X coordinate"
                      value={customPositionInputs.x}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomPositionInputs((prev) => ({
                          ...prev,
                          x: value,
                        }));
                        if (value !== "") {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setCustomTextPosition((prev) => ({
                              ...prev,
                              x: numValue,
                            }));
                          }
                        }
                      }}
                      onBlur={() => {
                        if (customPositionInputs.x === "") {
                          setCustomPositionInputs((prev) => ({
                            ...prev,
                            x: customTextPosition.x.toString(),
                          }));
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      type="number"
                      placeholder="Y coordinate"
                      value={customPositionInputs.y}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomPositionInputs((prev) => ({
                          ...prev,
                          y: value,
                        }));
                        if (value !== "") {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setCustomTextPosition((prev) => ({
                              ...prev,
                              y: numValue,
                            }));
                          }
                        }
                      }}
                      onBlur={() => {
                        if (customPositionInputs.y === "") {
                          setCustomPositionInputs((prev) => ({
                            ...prev,
                            y: customTextPosition.y.toString(),
                          }));
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                  </Stack>
                </Grid>
              )}

              {/* Text Styling */}
              {showSizeText && (
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    Text Style
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Font Size"
                      type="number"
                      value={fontSizeInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFontSizeInput(value);
                        if (value !== "") {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            setTextStyle((prev) => ({
                              ...prev,
                              fontSize: numValue,
                            }));
                          }
                        }
                      }}
                      onBlur={() => {
                        if (fontSizeInput === "") {
                          setFontSizeInput(textStyle.fontSize.toString());
                        }
                      }}
                      inputProps={{ min: 8, max: 72 }}
                      size="small"
                      fullWidth
                    />
                    {/* <Box>
                      <Typography
                        variant="caption"
                        display="block"
                        gutterBottom
                      >
                        Text Color
                      </Typography>
                      <input
                        type="color"
                        value={textStyle.color}
                        onChange={(e) =>
                          setTextStyle((prev) => ({
                            ...prev,
                            color: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          height: "40px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </Box> */}
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Instructions */}
        <Paper elevation={1} sx={{ p: 3, mt: 4, bgcolor: "primary.50" }}>
          <Typography variant="h6" gutterBottom sx={{ color: "primary.800" }}>
            How to Use:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Upload a background image (PNG, JPG, or WebP)" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Upload a logo image" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DragIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Drag the logo to position it on the background" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GridIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Use the grid overlay for precise positioning (10px snap)" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Adjust logo size using the width/height controls" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <RotateRightIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Rotate the logo using the rotation controls or quick buttons" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <TextIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Toggle size text display and customize its position and style" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DownloadIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Click 'Download Composite' to save the final image with all elements" />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tips:</strong> Use the grid for precise alignment •
              Text can be positioned relative to logo or at custom coordinates •
              Rotation works with all other features • Export includes all
              elements at full resolution
            </Typography>
          </Alert>
        </Paper>
      </Container>

      {/* Hidden canvas for export */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </Box>
  );
};

export default MockupGenerator;
